import { PRODUCT_TYPES, type Investor } from '@/types/investor';

export type CanonicalInvestorProduct = typeof PRODUCT_TYPES[number];

const PRODUCT_ALIASES: Record<string, CanonicalInvestorProduct | CanonicalInvestorProduct[]> = {
  conventional: 'Conforming',
  conforming: 'Conforming',
  qm: 'Conforming',
  fha: 'FHA',
  va: 'VA',
  jumbo: 'Jumbo',
  'non-qm': 'Non-QM',
  'non qm': 'Non-QM',
  nonqm: 'Non-QM',
  usda: 'USDA',
  dscr: 'DSCR',
  reverse: 'Reverse',
  '2nd/heloc': ['2nd Mortgage', 'HELOC'],
  '2nd / heloc': ['2nd Mortgage', 'HELOC'],
  'second/heloc': ['2nd Mortgage', 'HELOC'],
  'second mortgage': '2nd Mortgage',
  '2nd mortgage': '2nd Mortgage',
  '2nd': '2nd Mortgage',
  heloc: 'HELOC',
  itin: 'ITIN',
  'foreign national': 'Foreign National',
  construction: 'Construction',
  commercial: 'Commercial',
  '5-8 unit': '5-8 Units',
  '5-8 units': '5-8 Units',
  '5 to 8 units': '5-8 Units'
};

const PRODUCT_SET = new Set<string>(PRODUCT_TYPES);

function normalizeKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function fuzzyProductMatch(value: string): CanonicalInvestorProduct[] {
  const key = normalizeKey(value);
  const has = (pattern: RegExp) => pattern.test(key);

  if (has(/\bnon[\s-]?qm\b/) || has(/\bnonqualified\b/)) {
return ['Non-QM'];
}
  if (has(/\bheloc\b/)) {
return ['HELOC'];
}
  if (has(/\b(2nd|second)\b/) && has(/\b(mortgage|lien|loan)\b/)) {
return ['2nd Mortgage'];
}
  if (has(/\bfha\b/)) {
return ['FHA'];
}
  if (has(/\bva\b/)) {
return ['VA'];
}
  if (has(/\busda\b/)) {
return ['USDA'];
}
  if (has(/\bdscr\b/)) {
return ['DSCR'];
}
  if (has(/\bjumbo\b/)) {
return ['Jumbo'];
}
  if (has(/\breverse\b/)) {
return ['Reverse'];
}
  if (has(/\bitin\b/)) {
return ['ITIN'];
}
  if (has(/\bforeign\s+national\b/)) {
return ['Foreign National'];
}
  if (has(/\bconstruction\b/)) {
return ['Construction'];
}
  if (has(/\bcommercial\b/)) {
return ['Commercial'];
}
  if (has(/\b5[\s-]*(to|-)?\s*8\s+units?\b/)) {
return ['5-8 Units'];
}
  if (has(/\b(conforming|conventional|fnma|fannie|fhlmc|freddie|agency|qm)\b/)) {
return ['Conforming'];
}

  return [];
}

export function normalizeInvestorProduct(value: string | null | undefined): CanonicalInvestorProduct[] {
  if (!value) {
    return [];
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }
  if (PRODUCT_SET.has(trimmed)) {
    return [trimmed as CanonicalInvestorProduct];
  }
  const alias = PRODUCT_ALIASES[normalizeKey(trimmed)];
  if (alias) {
    return Array.isArray(alias) ? alias : [alias];
  }
  return fuzzyProductMatch(trimmed);
}

export function normalizeInvestorProducts(values: Array<string | null | undefined> | null | undefined): CanonicalInvestorProduct[] {
  const seen = new Set<CanonicalInvestorProduct>();
  const out: CanonicalInvestorProduct[] = [];
  (values ?? []).forEach((value) => {
    normalizeInvestorProduct(value).forEach((product) => {
      if (!seen.has(product)) {
        seen.add(product);
        out.push(product);
      }
    });
  });
  return out;
}

export function getInvestorProducts(investor: Investor, channel: 'wholesale' | 'correspondent' | 'all' = 'all'): CanonicalInvestorProduct[] {
  if (channel === 'wholesale') {
    return normalizeInvestorProducts(
      investor.wholesaleProducts?.length ? investor.wholesaleProducts : investor.productType
    );
  }
  if (channel === 'correspondent') {
    return normalizeInvestorProducts(
      investor.correspondentProducts?.length ? investor.correspondentProducts : investor.productType
    );
  }
  const channelProducts = normalizeInvestorProducts([
    ...(investor.wholesaleProducts ?? []),
    ...(investor.correspondentProducts ?? [])
  ]);
  return channelProducts.length > 0
    ? channelProducts
    : normalizeInvestorProducts(investor.productType);
}

export function formatInvestorProducts(investor: Investor, channel: 'wholesale' | 'correspondent' | 'all' = 'all'): string {
  return getInvestorProducts(investor, channel).join(', ');
}
